<template>
  <div :class="$style.hero">
    <!-- 背景层 -->
    <div
      :class="$style['hero-background']"
      :style="{
        backgroundImage: currentPoster ? `url(${currentPoster})` : 'none',
      }"
    >
      <video
        v-if="backgroundVideo"
        autoplay
        muted
        loop
        playsinline
        :poster="currentPoster"
      >
        <source :src="backgroundVideo" type="video/mp4" />
      </video>
    </div>

    <!-- Logo -->
    <div v-if="!isPortraitMode" :class="$style['hero-logo']">
      <img :src="logoVerticalWhiteRed" />
    </div>

    <!-- 内容容器 -->
    <div :class="$style['hero-content']">
      <!-- Slogan -->
      <div :class="$style['hero-slogan']">
        <img src="./images/slogan.png" />
      </div>

      <!-- CTA按钮 -->
      <button
        v-if="promoVideo"
        :class="$style['hero-cta']"
        @click="handlePlayClick"
      />
    </div>

    <!-- 滚动提示箭头 -->
    <scroll-indicator :class="$style['scroll-indicator']" />

    <!-- 视频播放模态层 -->
    <div
      v-if="showVideoModal"
      :class="$style['video-modal']"
      @click="closeVideoModal"
    >
      <div :class="$style['video-modal-content']" @click.stop>
        <!-- 关闭按钮 -->
        <button :class="$style['video-modal-close']" @click="closeVideoModal" />

        <!-- 模态层视频播放器 -->
        <div :class="$style['video-modal-player']">
          <video
            ref="videoPlayer"
            controls
            autoplay
            preload="metadata"
            @ended="onVideoEnded"
          >
            <source :src="promoVideo" type="video/mp4" />
          </video>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ScrollIndicator from '../ScrollIndicator'

export default {
  name: 'LandingHero',
  components: {
    ScrollIndicator,
  },
  data() {
    return {
      // 是否为竖屏模式
      isPortraitMode: false,
      // 是否显示视频模态层
      showVideoModal: false,
      // fullpage.js 实例引用
      fullpageInstance: null,
    }
  },
  computed: {
    ...mapGetters([
      'logoVerticalWhiteRed',
      'backgroundVideoForLanding',
      'promoVideoForLanding',
    ]),
    // 根据屏幕方向返回对应的poster图片
    currentPoster() {
      return this.isPortraitMode
        ? this.backgroundVideoForLanding.portrait_poster
        : this.backgroundVideoForLanding.landscape_poster
    },
    // 获取背景视频数据
    backgroundVideo() {
      return this.isPortraitMode
        ? this.backgroundVideoForLanding.portrait_bg
        : this.backgroundVideoForLanding.landscape_bg
    },
    // 获取推广视频数据
    promoVideo() {
      return this.promoVideoForLanding.mp4
    },
  },
  watch: {
    // 监听视频模态层的显示/隐藏状态，控制fullpage滚动行为
    showVideoModal(isVisible) {
      if (!this.fullpageInstance) return

      if (isVisible) {
        // 模态层打开时禁用 fullpage 滚动
        this.fullpageInstance.setAllowScrolling(false)
        this.fullpageInstance.setKeyboardScrolling(false)
      } else {
        // 模态层关闭时恢复 fullpage 滚动
        this.fullpageInstance.setAllowScrolling(true)
        this.fullpageInstance.setKeyboardScrolling(true)
      }
    },
  },
  mounted() {
    // 添加全局事件监听器
    this.$root.$on('screen-info-changed', this.handleScreenInfoChange)
  },
  beforeDestroy() {
    // 组件销毁时恢复 fullpage 滚动
    if (this.fullpageInstance) {
      this.fullpageInstance.setAllowScrolling(true)
      this.fullpageInstance.setKeyboardScrolling(true)
    }

    // 移除全局事件监听器
    this.$root.$off('screen-info-changed', this.handleScreenInfoChange)
  },
  methods: {
    handlePlayClick() {
      this.showVideoModal = true
    },
    // 关闭视频模态层
    closeVideoModal() {
      this.showVideoModal = false
      // 暂停视频播放
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        this.$refs.videoPlayer.currentTime = 0
      }
    },
    // 视频播放结束处理
    onVideoEnded() {
      // 延迟关闭模态层，让用户看到结束画面
      setTimeout(() => {
        this.closeVideoModal()
      }, 1000)
    },
    // 处理屏幕方向变化的事件监听器
    handleScreenInfoChange(newScreenInfo) {
      if (!newScreenInfo) {
        return
      }
      this.isPortraitMode = newScreenInfo.type !== 'landscape'
    },
    // 设置 fullpage.js 实例引用（由父组件调用）
    setFullpageInstance(instance) {
      this.fullpageInstance = instance
    },
  },
}
</script>

<style lang="less" module>
.hero {
  .size(100%);

  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 背景视频 */
.hero-background {
  .size(100%);

  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  /* 背景视频 */
  video {
    .size(100%);

    object-fit: cover;
  }

  /* 视频遮罩层 */
  &::after {
    .size(100%);

    content: '';
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }
}

/* Logo样式 */
.hero-logo {
  .size(1.17rem, 0.93rem);

  position: absolute;
  top: 0.5rem;
  left: 0.8rem;
  z-index: 3;
  opacity: 0;

  img {
    .size(100%);

    object-fit: contain;
  }
}

/* 内容容器 */
.hero-content {
  z-index: 2;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Slogan */
.hero-slogan {
  opacity: 0;

  img {
    .size(100%);

    object-fit: contain;
  }
}

/* CTA按钮样式 */
.hero-cta {
  background: url('./images/btn-player.png') no-repeat center;
  background-size: 100% 100%;
  opacity: 0;
}

/* 滚动指示器 */
.scroll-indicator {
  opacity: 0;
  left: 0;
  right: 0;
  bottom: 0.64rem;
  margin: 0 auto;
}

/* 视频模态层 */
.video-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;

  /* 视频模态层内容 */
  &-content {
    position: relative;
    aspect-ratio: 16 / 9;
    animation: slideUp 0.3s ease;
  }

  /* 关闭按钮 */
  &-close {
    .size(0.3rem);

    position: absolute;
    top: -0.5rem;
    right: 0;
    background: url('./images/btn-close.png') no-repeat center;
    background-size: 100% 100%;
    border: none;
    cursor: pointer;
    z-index: 10001;
    transition: transform 0.3s ease;

    &:hover {
      transform: rotate(90deg);
    }
  }

  /* 模态层视频播放器 */
  &-player {
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: #000000;
    }
  }
}

/* 模态层动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

// 横屏
:global(.landing-screen-landscape) {
  .hero-content {
    position: absolute;
    bottom: 3.2rem;
    left: 0;
    right: 0;
    margin: auto;
  }

  .hero-slogan {
    .size(10.4rem, 6.25rem);
  }

  .hero-cta {
    .size(1.46rem, 0.61rem);

    margin-top: -0.5rem;
  }

  .video-modal-content {
    width: 12.8rem;
    max-width: 1920px;
    max-height: 1080px;
  }

  .hero-background::after {
    background: url('./images/landscape-mask.png') no-repeat center;
    background-size: cover;
  }

  .scroll-indicator {
    &::before {
      background-image: url('./images/scroll-indicator.png');
    }
  }
}

// 竖屏
:global(.landing-screen-portrait) {
  .hero-slogan {
    .size(10.73rem, 6.45rem);
  }

  .hero-cta {
    .size(2.41rem, 1.01rem);

    margin-top: 0;
  }

  .video-modal {
    &-content {
      width: 100%;
    }

    &-close {
      display: none;
    }
  }

  .hero-background::after {
    background: url('./images/portrait-mask.png') no-repeat center;
    background-size: cover;
  }

  .scroll-indicator {
    &::before {
      background-image: url('./images/scroll-indicator-small.png');
    }
  }
}

// fullpage.js 完全进入页面时的动画效果
:global(.fp-completely) {
  .hero-logo {
    animation: fadeInDown 1s ease forwards;
  }

  .hero-slogan {
    animation: fadeInDown 1s ease forwards;
  }

  .hero-cta {
    animation: fadeInDown 1s ease forwards;
    animation-delay: 0.5s;
  }

  .scroll-indicator {
    animation: fadeInDown 1s ease forwards;
    animation-delay: 1s;
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-0.3rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
